import { Link } from "react-router-dom";
import { ArrowUpRight, Handshake, Sparkles } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { partners } from "@/data/partners";
import { SALTY_SPIRIT, saltyExperiences } from "@/data/salty-spirit";

export default function PartnersTeaser() {
  const { lang } = useLanguage();
  const isEs = lang === "es";
  const featured = partners.slice(0, 3);
  const lux = isEs ? SALTY_SPIRIT.es : SALTY_SPIRIT.en;
  const luxThumbs = saltyExperiences.filter((e) => e.featured).slice(0, 3);

  return (
    <section
      className="border-t border-[#E5DACC] bg-[#FAF7F2] pt-16 pb-10 md:pt-20 md:pb-12"
      data-testid="partners-teaser"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="mb-8 flex flex-col items-start justify-between gap-5 md:mb-10 md:flex-row md:items-end">
          <div className="max-w-xl">
            <p className="mb-3 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#4A4A4A]">
              <Handshake size={13} />
              {isEs ? "También podría interesarte" : "You may also like"}
            </p>
            <h2
              className="text-3xl leading-tight tracking-tight md:text-4xl"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              {isEs ? "Aventuras aliadas" : "Partner adventures"}
            </h2>
            <p className="mt-3 max-w-md text-sm text-[#4A4A4A] md:text-base">
              {isEs
                ? "Guías locales de confianza en toda la costa oeste — cada uno lo hemos probado."
                : "Trusted local guides across the west coast — we've tested every one."}
            </p>
          </div>
          <Link
            to="/partners"
            data-testid="partners-teaser-see-all"
            className="inline-flex items-center gap-2 rounded-full border border-[#0A0A0A]/15 bg-white px-5 py-2.5 text-sm font-semibold text-[#0A0A0A] transition-all hover:-translate-y-0.5 hover:border-[#FF5533] hover:text-[#FF5533]"
          >
            {isEs ? "Ver todas" : "See all partners"}
            <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {featured.map((p) => {
            const t = p[isEs ? "es" : "en"];
            return (
              <Link
                key={p.slug}
                to={`/partner/${p.slug}`}
                data-testid={`partners-teaser-card-${p.slug}`}
                className="group flex flex-col overflow-hidden rounded-2xl border border-[#E5DACC] bg-white transition-all hover:-translate-y-1 hover:border-[#FF5533]/40 hover:shadow-xl"
              >
                <div className="relative h-44 overflow-hidden md:h-48">
                  <img
                    src={p.cover}
                    alt={t.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                  <div className="absolute left-3 top-3 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#0A0A0A]">
                    {t.tag}
                  </div>
                  <div className="absolute inset-x-3 bottom-3 text-xs text-white/95">
                    <span>{p.place}</span>
                  </div>
                </div>
                <div className="flex flex-1 flex-col gap-2 p-5">
                  <h3
                    className="text-lg leading-tight tracking-tight"
                    style={{ fontFamily: "Fraunces, serif" }}
                  >
                    {t.name}
                  </h3>
                  <span className="mt-auto inline-flex items-center gap-1.5 text-xs font-semibold text-[#FF5533]">
                    {isEs ? "Ver y reservar" : "View & book"}
                    <ArrowUpRight size={12} className="transition-transform group-hover:rotate-45" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Luxury experience — Salty Spirit (Vieques) */}
        <Link
          to="/luxury"
          data-testid="home-luxury-teaser"
          className="group relative mt-6 flex flex-col gap-6 overflow-hidden rounded-3xl border border-[#FFB800]/25 bg-[#0A0A0A] p-6 text-white transition-all hover:-translate-y-1 hover:border-[#FFB800]/50 hover:shadow-2xl hover:shadow-black/20 md:mt-8 md:flex-row md:items-center md:p-8"
        >
          <div
            className="absolute inset-0 -z-10 bg-cover bg-center opacity-25"
            style={{ backgroundImage: `url(${SALTY_SPIRIT.cover})` }}
            aria-hidden="true"
          />
          <div className="pointer-events-none absolute -right-16 -top-16 -z-10 h-64 w-64 rounded-full bg-[#FFB800]/20 blur-[110px]" />

          <div className="flex-1">
            <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#FFB800]/40 bg-[#FFB800]/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#FFB800]">
              <Sparkles size={11} /> {lux.eyebrow}
            </p>
            <h3
              className="max-w-lg text-2xl leading-tight tracking-tight md:text-3xl"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              {lux.title}
            </h3>
            <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#FFB800]">
              {lux.cta}
              <ArrowUpRight size={15} className="transition-transform group-hover:rotate-45" />
            </span>
          </div>

          <div className="flex shrink-0 gap-3">
            {luxThumbs.map((e) => (
              <div key={e.slug} className="relative hidden h-24 w-24 overflow-hidden rounded-2xl ring-1 ring-white/15 sm:block md:h-28 md:w-28">
                <img
                  src={e.cover}
                  alt={e[isEs ? "es" : "en"].name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                />
              </div>
            ))}
          </div>
        </Link>
      </div>
    </section>
  );
}
